import React, { useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, Center, Container, Flex, Group, Image, Stack, Text, Title } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";

import { openContactUsForm } from "@/components/ContactUsForm/ContactUsForm";
// core components
import { TitleContainer } from "@/components/HeaderContainer";
import CompletedCourseSection from "@/components/LearnerPortal/CompletedCourseSection";
import GreetingText from "@/components/LearnerPortal/CourseHeader";
import UpcomingCoursesCard from "@/components/LearnerPortal/UpcomingCoursesCard";
import { ThemedButton } from "@/components/ThemedButton";
import ThemedCarousel from "@/components/ThemedCarousel";

import generalApi from "@/services/general-api";

import { AVAILABLE_COURSES_LINKS as AVAILABLE_COURSES_LINK } from "@/utilities/constant";

import AnimationIcon5 from "./assets/ai.png";
import EmptyUpcomingCoursesPlaceholderImage from "./assets/landing-page-girl-study.png";
import AnimationIcon1 from "./assets/leadership.png";
import AnimationIcon3 from "./assets/people-management.png";
import AnimationIcon4 from "./assets/reporing.png";
import AnimationIcon2 from "./assets/time-management.png";
import AnimationIcon6 from "./assets/voice-input.png";

function Dashboard() {
	// Use React Query for data fetching
	const {
		data: allupcomingCourses = {},
		isLoading: isUpcomingLoading,
		error: upcomingError,
		isFetched,
	} = useQuery({
		queryKey: ["upcomingCourses"],
		queryFn: () => generalApi.getUpcomingCourse(),
		retry: 1,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		let msg = getQueryVariable("msg");
		let msg_status = getQueryVariable("status");
		if (msg) {
			// GTMCheckoutSFCPaymentFailed(checkoutAnalyticsData, userData);
			if (msg_status == 1) {
				toast.success(decodeURI(msg));
			} else {
				toast.error(decodeURI(msg));
			}
		}
	}, []);

	function getQueryVariable(variable) {
		var query = window.location.search.substring(1);
		var vars = query.split("&");
		for (var i = 0; i < vars.length; i++) {
			var pair = vars[i].split("=");
			if (pair[0] == variable) {
				return pair[1];
			}
		}
		return false;
	}
	if (upcomingError) {
		return (
			<div className="text-center py-5">
				<h3>Error loading courses</h3>
				<p>Please try refreshing the page.</p>
			</div>
		);
	}

	return (
		<div>
			<TitleContainer pt={10} pb={0}>
				<GreetingText />
			</TitleContainer>

			<Stack gap={40}>
				{isUpcomingLoading ? (
					<LoadingPlaceholder />
				) : (
					<>
						{!allupcomingCourses?.upcoming?.length &&
							!allupcomingCourses?.unverified?.length &&
							!allupcomingCourses?.tobe_reschedule?.length && <EmptyUpcomingCoursesPlaceholder />}

						{allupcomingCourses?.upcoming?.length > 0 && (
							<ThemedCarousel title="Upcoming Courses">
								{allupcomingCourses.upcoming.map((course, i) => (
									<Carousel.Slide key={i}>
										<Center>
											<Container size={"xl"}>
												<UpcomingCoursesCard course={course} status="upcoming" />
											</Container>
										</Center>
									</Carousel.Slide>
								))}
							</ThemedCarousel>
						)}

						{allupcomingCourses?.unverified && allupcomingCourses?.unverified?.length > 0 && (
							<ThemedCarousel title="Pending Courses">
								{allupcomingCourses.unverified.map((course, i) => (
									<Carousel.Slide key={i}>
										<Center>
											<Container size={"xl"}>
												<UpcomingCoursesCard course={course} status="pending" />
											</Container>
										</Center>
									</Carousel.Slide>
								))}
							</ThemedCarousel>
						)}

						{allupcomingCourses?.tobe_reschedule?.length > 0 && (
							<ThemedCarousel title="To Be Rescheduled">
								{allupcomingCourses.tobe_reschedule.map((course, i) => (
									<Carousel.Slide key={i}>
										<Center>
											<Container size={"xl"}>
												<UpcomingCoursesCard course={course} status="to_be_reschedule" />
											</Container>
										</Center>
									</Carousel.Slide>
								))}
							</ThemedCarousel>
						)}
					</>
				)}
				<CompletedCourseSection isReady={isFetched} />
			</Stack>
		</div>
	);
}

export const EmptyUpcomingCoursesPlaceholder = () => {
	const handleEnquireNow = () => {
		openContactUsForm(
			{},
			{
				zIndex: 10_000,
			},
		);
	};

	return (
		<Box
			bg="white"
			p={{
				base: 10,
				sm: 20,
				md: 50,
			}}
			mt={20}>
			<Container size="lg">
				<Flex
					direction={{ base: "column", sm: "row" }}
					align={{ base: "center", sm: "start" }}
					justify={"center"}
					py={20}>
					<Box
						display={"flex"}
						flex={"1 1 40%"}
						p={10}
						style={{ textAlign: "center" }}
						pos="relative">
						<Image
							w="100%"
							src={EmptyUpcomingCoursesPlaceholderImage}
							style={{ aspectRatio: 4 / 3 }}
							fit="cover"
						/>

						<div>
							<motion.div
								initial={{ opacity: 0.2, position: "absolute", top: "30%", left: 0, x: -5 }}
								whileInView={{ x: 0, opacity: 1, scale: 0.8 }}
								viewport={{ once: true }}
								animate={{ y: [-10, 10, -10] }}
								transition={{
									duration: 0.5,
									ease: "easeOut",
									y: { repeat: Infinity, duration: 3, ease: "easeInOut" },
								}}>
								<img src={AnimationIcon1} style={{ aspectRatio: 1, width: 100 }} />
							</motion.div>
							<motion.div
								initial={{ opacity: 0.2, position: "absolute", top: "50%", left: 0, x: 20 }}
								whileInView={{ x: 40, opacity: 1, scale: 0.9 }}
								viewport={{ once: true }}
								animate={{ y: [-8, 8, -8] }}
								transition={{
									duration: 0.5,
									ease: "easeOut",
									y: { repeat: Infinity, duration: 2.5, ease: "easeInOut" },
								}}>
								<img src={AnimationIcon2} style={{ aspectRatio: 1, width: 100 }} />
							</motion.div>
							<motion.div
								initial={{ opacity: 0.2, position: "absolute", top: "28%", left: 0, x: 60 }}
								whileInView={{ x: 80, opacity: 1, scale: 1 }}
								viewport={{ once: true }}
								animate={{ y: [-12, 12, -12] }}
								transition={{
									duration: 0.5,
									ease: "easeOut",
									y: { repeat: Infinity, duration: 3.5, ease: "easeInOut" },
								}}>
								<img src={AnimationIcon3} style={{ aspectRatio: 1, width: 100 }} />
							</motion.div>
						</div>

						<div>
							<motion.div
								initial={{ opacity: 0.2, position: "absolute", top: "10%", rigth: 0, x: -120 }}
								whileInView={{ x: -150, opacity: 1 }}
								viewport={{ once: true }}
								animate={{ y: [-6, 6, -6] }}
								transition={{
									duration: 0.5,
									ease: "easeOut",
									y: { repeat: Infinity, duration: 2.8, ease: "easeInOut" },
								}}>
								<img
									src={AnimationIcon5}
									style={{ aspectRatio: 1, width: 100, objectFit: "contain" }}
								/>
							</motion.div>
							<motion.div
								initial={{ opacity: 0.2, position: "absolute", top: "28%", rigth: 0, x: -160 }}
								whileInView={{ x: -190, opacity: 1 }}
								viewport={{ once: true }}
								animate={{ y: [-9, 9, -9] }}
								transition={{
									duration: 0.5,
									ease: "easeOut",
									y: { repeat: Infinity, duration: 3.2, ease: "easeInOut" },
								}}>
								<img
									src={AnimationIcon4}
									style={{ aspectRatio: 1, width: 100, objectFit: "contain" }}
								/>
							</motion.div>
							<motion.div
								initial={{ opacity: 0.2, position: "absolute", top: "20%", rigth: 0, x: -80 }}
								whileInView={{ x: -100, opacity: 1 }}
								viewport={{ once: true }}
								animate={{ y: [-7, 7, -7] }}
								transition={{
									duration: 0.5,
									ease: "easeOut",
									y: { repeat: Infinity, duration: 2.7, ease: "easeInOut" },
								}}>
								<img
									src={AnimationIcon6}
									style={{ aspectRatio: 1, width: 100, objectFit: "contain" }}
								/>
							</motion.div>
						</div>
					</Box>
					<Box flex={"1 1 50%"} mt={{ base: 10, sm: 0 }} px={{ base: 0, sm: "30px" }}>
						<Title order={2}>No upcoming courses yet.</Title>
						<Text mt={20} c="gray.7">
							Ready to level up? Explore classes in AI and soft skills and start building 3-5
							employer-ready skillsets you can apply in your role. Learn from industry trainers,
							practice with guided learning activities and leave with new skills and a verifiable
							e-certificate to showcase your learning.
							<br />
							<br />
							Whether you're exploring a new path or strengthening your current one, our short,
							practical, outcomes-focused courses are designed to help you communicate your
							capabilities with confidence.
						</Text>
						<Group>
							<ThemedButton mt={20} onClick={handleEnquireNow}>
								Enquire Now
							</ThemedButton>
							<ThemedButton mt={20} variant="outline" component={Link} to={AVAILABLE_COURSES_LINK}>
								View Courses
							</ThemedButton>
						</Group>
					</Box>
				</Flex>
			</Container>
		</Box>
	);
};

export const LoadingPlaceholder = () => {
	return (
		<Box>
			<Center>
				<Stack ta="center" my={20} gap={5}>
					<Skeleton height={30} width={300} />
					<Skeleton height={30} width={150} />
				</Stack>
			</Center>
			<Box bg="white" py={50}>
				<Container bg="white">
					<Skeleton height={300} />
				</Container>
			</Box>
		</Box>
	);
};

export default Dashboard;
